// ============================================================================
// Site Management Types
// Matches backend models from hcp-admin-backend
// ============================================================================

// ============================================================================
// Core Site Types
// ============================================================================

export interface Site {
  id: number;
  store_id: number;
  alias: string;
  description?: string;
  dimension?: SiteDimension;
  max_length?: number;
  seasonal: boolean;
  type_id?: number;
  site_section_id?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
}

export interface SiteDimension {
  unit: 'feet' | 'meters';
  length?: number;
  width?: number;
  height?: number;
  note?: string;
}

export interface SiteWithDetails extends Site {
  site_type?: StoreSiteType;
  section?: StoreSiteSection;
  hookups: SiteHookup[];
  pricing: Pricing[];
  overage_fees: SiteOverageFee[];
  imposed_charges: SiteImposedCharge[];
  blackouts: SiteBlackout[];
}

// For display purposes in Sites nav
export interface SiteDisplayInfo extends Site {
  type_name?: string;
  section_name?: string;
  hookup_types?: string[];
  current_price?: number;
  fee_count?: number;
  status: SiteStatus;
}

export type SiteStatus = 'available' | 'occupied' | 'blocked' | 'seasonal';

// ============================================================================
// Site Type System
// ============================================================================

export interface StoreSiteType {
  id: number;
  store_id: number;
  name: string;
  description?: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
}

export interface StoreSiteTypeCreate {
  store_id?: number;  // Optional - backend infers from authenticated user if not provided
  name: string;
  description?: string;
  active?: boolean;
}

export interface StoreSiteTypeUpdate {
  name?: string;
  description?: string;
  active?: boolean;
}

// ============================================================================
// Site Sections
// ============================================================================

export interface StoreSiteSection {
  id: number;
  store_id: number;
  name: string;
  description?: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface StoreSiteSectionCreate {
  store_id?: number;
  name: string;
  description?: string;
  active?: boolean;
}

export interface StoreSiteSectionUpdate {
  name?: string;
  description?: string;
  active?: boolean;
}

// ============================================================================
// Hookup System
// ============================================================================

export type HookupType = 'water' | 'sewage' | 'electricity' | 'cable' | 'wifi';

export interface StoreHookupType {
  id: number;
  store_id: number;
  hookup_type: HookupType;
  display_name: string;
  default_amp?: number;
  active: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

export interface StoreHookupTypeCreate {
  store_id?: number;
  hookup_type: HookupType;
  display_name?: string;
  default_amp?: number;
  active?: boolean;
  display_order?: number;
}

export interface StoreHookupTypeUpdate {
  display_name?: string;
  default_amp?: number;
  active?: boolean;
  display_order?: number;
}

export interface SiteHookup {
  id: number;
  site_id: number;
  store_hookup_type_id: number;
  amp?: number;
  available: boolean;
  created_at?: string;
  updated_at?: string;
  // Joined
  hookup_type?: StoreHookupType;
}

export interface SiteHookupCreate {
  store_hookup_type_id: number;
  amp?: number;
  available?: boolean;
}

export interface SiteHookupUpdate {
  amp?: number;
  available?: boolean;
}

// ============================================================================
// Pricing System
// ============================================================================

export interface Pricing {
  id: number;
  site_id: number;
  store_id: number;
  price: number;
  weekend_price?: number;
  holiday_price?: number;
  effective_date?: string;
  end_date?: string;
  discount?: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PricingCreate {
  site_id: number;
  price: number;
  weekend_price?: number;
  holiday_price?: number;
  effective_date?: string;
  end_date?: string;
  discount?: number;
  active?: boolean;
}

export interface PricingUpdate {
  price?: number;
  weekend_price?: number;
  holiday_price?: number;
  effective_date?: string;
  end_date?: string;
  discount?: number;
  active?: boolean;
}

export interface StorePricing {
  id: number;
  store_id: number;
  site_type_id?: number;
  price: number;
  weekend_price?: number;
  holiday_price?: number;
  effective_date?: string;
  end_date?: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// Overage Fees
// ============================================================================

export type OverageFeeType = 'adult' | 'child' | 'pet' | 'vehicle';
export type FeeFrequency = 'once' | 'nightly' | 'weekly' | 'monthly' | 'yearly';

export interface SiteOverageFee {
  id: number;
  site_id: number;
  store_id: number;
  fee_category_id: number;
  fee_type: OverageFeeType;
  base_included: number;
  max_allowed: number;
  amount: number;
  flat: boolean;
  frequency: FeeFrequency;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SiteOverageFeeCreate {
  store_id: number;
  site_id: number;
  fee_category_id: number;
  fee_type: OverageFeeType;
  base_included: number;
  max_allowed: number;
  amount: number;
  flat?: boolean;
  frequency: FeeFrequency;
  active?: boolean;
}

export interface SiteOverageFeeUpdate {
  base_included?: number;
  max_allowed?: number;
  amount?: number;
  flat?: boolean;
  frequency?: FeeFrequency;
  active?: boolean;
}

export interface StoreOverageFee {
  id: number;
  store_id: number;
  fee_type: OverageFeeType;
  fee_category_id?: number; // Links to fee_charge_categories table
  base_included: number;
  max_allowed: number;
  amount: number;
  flat: boolean;
  frequency: FeeFrequency;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface StoreOverageFeeCreate {
  store_id?: number;
  fee_type: OverageFeeType;
  fee_category_id?: number; // Will be auto-mapped from fee_type
  base_included: number;
  max_allowed: number;
  amount: number;
  flat?: boolean;
  frequency: FeeFrequency;
  active?: boolean;
}

export interface StoreOverageFeeUpdate {
  base_included?: number;
  max_allowed?: number;
  amount?: number;
  flat?: boolean;
  frequency?: FeeFrequency;
  fee_category_id?: number;
  active?: boolean;
}

// ============================================================================
// Imposed Charges
// ============================================================================

export type FeeTrigger = 'any' | 'site_changed' | 'date_changed' | 'occupancy_changed' | 'site_rebooking';

export interface SiteImposedCharge {
  id: number;
  site_id: number;
  store_id: number;
  item: string;
  amount: number;
  flat: boolean;
  fee_trigger: FeeTrigger;
  frequency: FeeFrequency;
  active: boolean;
  category_id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface SiteImposedChargeCreate {
  site_id: number;
  item: string;
  amount: number;
  flat?: boolean;
  fee_trigger?: FeeTrigger;
  frequency: FeeFrequency;
  active?: boolean;
  category_id?: number;
}

export interface SiteImposedChargeUpdate {
  item?: string;
  amount?: number;
  flat?: boolean;
  fee_trigger?: FeeTrigger;
  frequency?: FeeFrequency;
  active?: boolean;
  category_id?: number;
}

export interface StoreCharge {
  id: number;
  store_id: number;
  item: string;
  amount: number;
  flat: boolean;
  fee_trigger: FeeTrigger;
  frequency: FeeFrequency;
  active: boolean;
  category_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// Blackouts
// ============================================================================

export type BlackoutType =
  | 'maintenance'
  | 'seasonal'
  | 'emergency'
  | 'weather'
  | 'other';

export interface SiteBlackout {
  id: number;
  site_id: number;
  store_id: number;
  blackout_type: BlackoutType;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SiteBlackoutCreate {
  site_id: number;
  store_id: number;
  blackout_type: BlackoutType;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  active?: boolean;
}

export interface SiteBlackoutUpdate {
  blackout_type?: BlackoutType;
  title?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  active?: boolean;
}

export interface StoreBlackout {
  id: number;
  store_id: number;
  blackout_type: BlackoutType;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface StoreBlackoutCreate {
  store_id?: number;
  blackout_type: BlackoutType;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  active?: boolean;
}

export interface StoreBlackoutUpdate {
  blackout_type?: BlackoutType;
  title?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  active?: boolean;
}

// ============================================================================
// Media Types
// ============================================================================

export type MediaType = 'image' | 'video';

/**
 * One media row, addressed by SLOT (`display_order`).
 *
 * Images: slot 0 is the thumbnail (a smaller rendition for site cards,
 * uploaded by the admin), slots 1..N are full-size photos. Videos occupy
 * slots 1..N. Gaps are normal — deleting slot 4 leaves 3 and 5 in place.
 */
export interface SiteMedia {
  id: number;
  store_id: number;
  media_type: MediaType;
  site_alias?: string;
  /** Slot number. 0 = thumbnail (images only). */
  display_order: number;
  /** Guest-facing text, used as `<img alt>` and captions. Max 125 chars. */
  description?: string | null;
  path: string;
  /**
   * Full public URL including a `?v=` cache-buster. Render VERBATIM — never
   * rebuild from `path` or strip the query string: extensions vary per upload
   * (.avif/.webp) and replaced files reuse the same object name, so without
   * `?v=` browsers keep serving the old image.
   */
  url?: string;
  created_by?: number;
  updated_by?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Business rules published by GET /api/medias/limits. Never hardcode these —
 * they are deployment configuration and the server enforces most of them.
 */
export interface MediaLimits {
  photos: {
    /** INCLUDES the thumbnail: 8 means slot 0 plus slots 1-7. */
    max_count: number;
    /** Cap on the CONVERTED file, not the source the admin picked. */
    max_bytes: number;
  };
  videos: {
    max_count: number;
    max_bytes: number;
    /** Client-enforced — check before converting, the server does not. */
    max_duration_seconds: number;
  };
  description_max_length: number;
  require_conversion: boolean;
  accepted_types?: string[];
}

// ============================================================================
// Site CRUD Types
// ============================================================================

export interface SiteCreate {
  alias: string;
  type_id: number;
  description?: string;
  dimension?: SiteDimension;
  max_length?: number;
  seasonal?: boolean;
  site_section_id?: number;
}

export interface SiteUpdate {
  alias?: string;
  type_id?: number;
  description?: string;
  dimension?: SiteDimension;
  max_length?: number;
  seasonal?: boolean;
  site_section_id?: number;
}

// ============================================================================
// Filter/Query Types
// ============================================================================

export interface SiteFilters {
  search?: string;
  type_id?: number;
  section_id?: number;
  status?: SiteStatus;
  hookup_types?: HookupType[];
}

// ============================================================================
// Hookup Icons Mapping
// ============================================================================

export const HOOKUP_ICONS: Record<HookupType, string> = {
  water: '💧',
  sewage: '🚽',
  electricity: '⚡',
  cable: '📺',
  wifi: '📶',
};

export const HOOKUP_LABELS: Record<HookupType, string> = {
  water: 'Water',
  sewage: 'Sewer',
  electricity: 'Electric',
  cable: 'Cable TV',
  wifi: 'WiFi',
};

export const BLACKOUT_TYPE_LABELS: Record<BlackoutType, string> = {
  maintenance: 'Maintenance',
  seasonal: 'Seasonal',
  emergency: 'Emergency',
  weather: 'Weather',
  other: 'Other',
};

export const FEE_FREQUENCY_LABELS: Record<FeeFrequency, string> = {
  once: 'One-time',
  nightly: 'Per Night',
  weekly: 'Per Week',
  monthly: 'Per Month',
  yearly: 'Per Year',
};

export const OVERAGE_FEE_TYPE_LABELS: Record<OverageFeeType, string> = {
  adult: 'Extra Adults',
  child: 'Extra Children',
  pet: 'Pets',
  vehicle: 'Extra Vehicles',
};
